import { CultureInfo } from "../../../Global/Dom/CultureInfo";
import { Localization } from "../../../OW.Remoting/Remoting/Localization";
import { GenericControl } from "../../UI.BaseControls/GenericControl";
import { createElement, registerCustomElement } from "../../UI.ElementarControls/Utils/CustomElementUtils";
import { CalendarType } from "../CalendarGrid";
import { CalendarCell } from "./CalendarCell";
import { CalendarEventItem, CalendarEventItemType } from "./CalendarEventItem";
/**
    * Ukázkový kód pro založení prázdného elementu
    */
export class MonthDayCell extends CalendarCell {
    private _isAttached: boolean;

    private _topElement: HTMLElement;
    private _dayNumberElement: HTMLSpanElement;
    private _hiddenEventItems: HTMLElement;
    private _moreEventsElement: MonthDayMore;
    private _isCurrentMonth = true;

    //#region Properties
    public set visible(v: boolean) {
        this.classList.toggle("Hidden", !v);
    }

    public get visible(): boolean {
        return !this.classList.contains("Hidden");
    }

    public set enabled(v: boolean) {
        this.classList.toggle("disabled", !v);
    }

    public get enabled(): boolean {
        return !this.classList.contains("disabled");
    }

    protected _interactive: boolean = true;

    public set interactive(v: boolean) {
        this._interactive = v;
    }

    public get interactive(): boolean {
        return this._interactive;
    }

    public get isAttached(): boolean {
        return this._isAttached;
    }

    public set dayText(v: string) {
        this._dayNumberElement.textContent = v;
    }

    public get dayText(): string {
        return this._dayNumberElement.textContent;
    }

    public override set idOfCell(v: Date) {
        this._idOfCell = v;
        if (this.dayText.trim().length === 0)
            this.dayText = v.getDate().toLocaleString(CultureInfo.getCultureInfo().name);

        this._dayNumberElement.addEventListener("click", () => {
            if (this._interactive) this.calendarChangeRequest.trigger(this, { type: CalendarType.day, date: this._idOfCell });
        });
        this._moreEventsElement.addEventListener("click", () => {
            if (this._interactive) this.calendarChangeRequest.trigger(this, { type: CalendarType.day, date: this._idOfCell });
        });

        this._updateStyles();
    }

    public override get idOfCell(): Date {
        return this._idOfCell;
    }

    public get isCurrentMonth(): boolean {
        return this._isCurrentMonth;
    }

    public set isCurrentMonth(v: boolean) {
        this._isCurrentMonth = v;
        this.classList.toggle("no-current-month", !v);
    }

    public get dayId(): string {
        return `12|${this.idOfCell.getDate()}|${this.idOfCell.getMonth()}|${this.idOfCell.getFullYear()}`;
    } // "12" na zacatku je defaultni cas pro eventy v mesicnim zobrazeni. Kdyztak se to musi zmenit v obou pripadech

    //#endregion Properties

    protected override async _updateStyles(): Promise<void> {
        this.classList.toggle("today", this.isToday);
        this.classList.toggle("weekend", this.isWeekend);
    }

    public override addEmptyItem(cvi: CalendarEventItem, insertTarget: "after" | "before", count: number = 1): void {
        let insertTargetElement = "beforebegin";
        if (insertTarget === "after")
            insertTargetElement = "afterend";

        for (let i = 0; i < count; i++) {
            const cvie = new CalendarEventItem();
            cvie.isEmpty = true;
            cvi.insertAdjacentElement(insertTargetElement as "afterend" | "beforebegin", cvie);
        }
    }

    public removeEmptyItems(): void {
        for (const item of Array.from(this._eventElement.children)) {
            if (item instanceof CalendarEventItem && item.isEmpty)
                item.remove();
        }
    }

    /**
        * prepocitani obsahu bunky, pokud se nevejdou vsechny polozky, zobrazi se informace o "X dalsich", ktere budou skryte a presunou se do skryteho bloku
        * @param lineHeight - velikost radku
        */
    public recalculateContent(lineHeight: number): void {
        const canShowCount = Math.floor((lineHeight - this._topElement.offsetHeight) / 25);
        const eventItems = Array.from(this._eventElement.children).concat(Array.from(this._hiddenEventItems.children));
        if (eventItems.indexOf(this._moreEventsElement) >= 0)
            eventItems.splice(eventItems.indexOf(this._moreEventsElement), 1);
        const itemsCount = eventItems.length;
        let itemsIn = 0;
        let itemsLeft = 0;
        let itemsMoreCount = 0;

        this._eventElement.innerHTML = "";
        this._hiddenEventItems.innerHTML = "";

        while (itemsIn < canShowCount - 1 && itemsCount - itemsIn > 0) {
            this._eventElement.appendChild(eventItems.shift());
            itemsIn++;
        }

        if (itemsIn + 1 === itemsCount) {
            const item = eventItems.shift() as CalendarEventItem;
            if (item.type === CalendarEventItemType.standalone) {
                this._eventElement.appendChild(item);
                itemsIn++;
            } else {
                this._hiddenEventItems.appendChild(item);
                if (!item.isEmpty)
                    itemsMoreCount++;
                itemsLeft++;
            }
        }

        while (itemsIn + itemsLeft < itemsCount) {
            const item = eventItems.shift() as CalendarEventItem;
            this._hiddenEventItems.appendChild(item);
            if (!item.isEmpty)
                itemsMoreCount++;
            itemsLeft++;
        }

        if (itemsMoreCount > 0) {
            this._moreEventsElement.number = itemsMoreCount;
            this._moreEventsElement.displayText();
            this._eventElement.appendChild(this._moreEventsElement);
        }
    }

    constructor(self?: any) {
        super();
        self ? self._constructor() : this._constructor(); // polyfill
    }

    protected override _constructor(): void {
        GenericControl._constructor(this as HTMLElement);

        this.classList.add("day-cell");
        this._dayNumberElement = createElement({ type: "span", classes: "day-number" }) as HTMLSpanElement;
        this._topElement = createElement({ classes: "top" });
        this._eventElement = createElement({ classes: "bottom event-content" });
        this._hiddenEventItems = createElement({});

        this._moreEventsElement = new MonthDayMore();

        this._topElement.appendChild(this._dayNumberElement);

        this.appendChild(this._topElement);
        this.appendChild(this._eventElement);
    }

    // ReSharper disable once InconsistentNaming
    protected connectedCallback(): void {
        this._isAttached = true;
        GenericControl.callEvent(this, "attached");
    }

    // ReSharper disable once InconsistentNaming
    protected disconnectedCallback(): void {
        this._isAttached = false;
        GenericControl.callEvent(this, "detached");
        this.unregisterEventListener();
    }

    /**
        * Vrátí pozici elementu v rámci dokumentu
        */
    public offset(): { top: number; left: number; width: number; height: number } {
        return GenericControl.offset(this as HTMLElement);
    }

    //#enregion Zpracování metod z globální třídy GenericControl
}

export class MonthDayMore extends HTMLElement {
    // @ts-ignore // nevyužito, ale ponecháno
    private _number: number;
    private _stringMore: string;
    private _stringMore2: string;

    public get number(): number {
        return this._number;
    }

    public set number(v: number) {
        this._number = v;
    }

    public async displayText(): Promise<void> {
        if (this.number <= 4) {
            this._stringMore = this._stringMore ?? await Localization.getTextPromise("GUI-649246"); // Další
            this.innerText = `${this.number} ${this._stringMore}`;
        } else {
            this._stringMore2 = this._stringMore2 ?? await Localization.getTextPromise("GUI-649247"); // Dalších
            this.innerText = `${this.number} ${this._stringMore2}`;
        }
    }

    constructor(self?: any) {
        super();
        self ? self._constructor() : this._constructor(); // polyfill
    }

    protected _constructor(): void {
        this.classList.add("btn-more");
    }
}

registerCustomElement("tsc-calendargridmonthdaycell", MonthDayCell);
registerCustomElement("tsc-calendargridmonthdaymore", MonthDayMore);
